import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import {
  BarChart3, ClipboardList, FlaskConical, Wrench, DollarSign,
  Loader2, Save, Trash2, ChevronLeft, Plus, Check, Clock,
  AlertTriangle, Shield, Droplets, Pencil, RefreshCw, X, Thermometer
} from 'lucide-react';
import { fetchSln, STATUS_META, API } from './nexusHelpers';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'priori', label: 'Baseline Forecast', icon: FlaskConical },
  { id: 'inspection', label: 'Inspection Data', icon: ClipboardList },
  { id: 'posteriori', label: 'Updated Forecast', icon: Shield },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench, disabled: true },
  { id: 'financial', label: 'Financial', icon: DollarSign, disabled: true },
];

const RACC_TABLE = {
  CEM_I: { 0.40: [800, 240], 0.45: [1100, 340], 0.50: [1577, 764], 0.55: [2700, 950], 0.60: [4500, 1300] },
  CEM_II_A_L: { 0.40: [1200, 360], 0.45: [1800, 560], 0.50: [2800, 880], 0.55: [4000, 1200], 0.60: [6200, 1700] },
  CEM_II_B_L: { 0.40: [1500, 450], 0.45: [2200, 680], 0.50: [3400, 1020], 0.55: [5000, 1600], 0.60: [7500, 2100] },
  CEM_III_A: { 0.40: [1800, 550], 0.45: [3000, 900], 0.50: [5300, 1600], 0.55: [7800, 2400], 0.60: [11000, 3400] },
  CEM_III_B: { 0.40: [3000, 900], 0.45: [5000, 1500], 0.50: [8900, 2930], 0.55: [13000, 4200], 0.60: [19000, 6000] },
};
const CEMENT_LABELS = { CEM_I: 'CEM I', CEM_II_A_L: 'CEM II/A-L', CEM_II_B_L: 'CEM II/B-L', CEM_III_A: 'CEM III/A', CEM_III_B: 'CEM III/B' };

function getRacc(cement, wc) {
  const ct = RACC_TABLE[cement];
  if (!ct) return [1577, 764];
  return ct[wc] || ct[0.50] || [1577, 764];
}

/* ── XHR engine call ── */
function callEngine(params) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/carbonation-risk/analyze`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = () => {
      let d = {};
      try { d = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 400) return reject(new Error(d.detail || 'Analysis failed'));
      resolve(d);
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(JSON.stringify(params));
  });
}

const NexusElement = ({ element, asset, zone, t, isExpired, onRefresh, onBack }) => {
  const [tab, setTab] = useState('overview');
  const [inspections, setInspections] = useState([]);
  const [runs, setRuns] = useState([]);

  const loadInspections = useCallback(async () => {
    try { setInspections(await fetchSln(`/api/sln/elements/${element.id}/inspections`)); } catch { setInspections([]); }
  }, [element.id]);
  const loadRuns = useCallback(async () => {
    try { setRuns(await fetchSln(`/api/sln/elements/${element.id}/runs`)); } catch { setRuns([]); }
  }, [element.id]);
  useEffect(() => { loadInspections(); loadRuns(); }, [loadInspections, loadRuns]);

  const statusMeta = STATUS_META[element.status] || STATUS_META.unknown;
  const env = asset?.env_conditions || {};

  /* ── Build engine params from element + asset env ── */
  const buildParams = useCallback((module) => {
    const [racc_lookup_mean, racc_lookup_std] = getRacc(element.cement_type, element.wc_ratio);
    const envOv = element.env_overrides || {};
    const m = { ...env, ...envOv };
    const base = {
      asset_name: asset?.asset_name || '', element_type: element.element_type || '',
      assessment_horizon: element.design_life || 50, xc_class: element.exposure_class || 'XC3',
      bar_diameter: 16, structure_age: null,
      cover_mean: element.cover_mean || 35, cover_std: element.cover_std || 8,
      racc_mean: element.racc_mean || racc_lookup_mean,
      racc_std: element.racc_std || racc_lookup_std,
      rh_mean: m.rh_mean || 70, rh_std: m.rh_std || 12,
      rh_min: m.rh_min || 40, rh_max: m.rh_max || 100,
      rh_distribution: m.rh_distribution || 'beta',
      co2_mean: m.co2_mean || 0.00082, co2_std: m.co2_std || 0.0001,
      kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
      bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
      tow: m.tow || 0, psr: m.psr || 0, curing_days: 7, epsilon_xc_std: 2.0,
      prior_method: 'SORM', posterior_method: 'FORM',
    };
    if (module === 'A') {
      base.inspection_age = null; base.xc_measured_mean = null; base.xc_measured_std = null;
    }
    if (module === 'C' && inspections.length > 0) {
      const latest = inspections[0];
      const inspYear = new Date(latest.inspection_date).getFullYear();
      const commYear = element.commissioning_year || asset?.commissioning_year || (inspYear - 30);
      base.inspection_age = latest.age_at_inspection || (inspYear - commYear);
      base.xc_measured_mean = latest.mean;
      base.xc_measured_std = latest.std_dev > 0 ? latest.std_dev : 3.0;
    }
    return base;
  }, [element, asset, env, inspections]);

  /* ── Auto-compute & cache ── */
  const autoCompute = useCallback(async (module) => {
    const params = buildParams(module);
    if (module === 'C' && (!params.xc_measured_mean || !params.inspection_age)) return null;
    try {
      const data = await callEngine(params);
      if (!data.success) return null;
      // Cache on element
      const cacheKey = module === 'A' ? 'cached_baseline' : 'cached_updated';
      await fetchSln(`/api/sln/elements/${element.id}`, {
        method: 'PATCH', body: JSON.stringify({ [cacheKey]: data }),
      });
      // Also save as live forecast
      const summary = data.summary || {};
      const beta = summary.updated_beta || summary.baseline_beta;
      await fetchSln(`/api/sln/elements/${element.id}/runs`, {
        method: 'POST', body: JSON.stringify({
          module, input_params: params, results: data, is_live: true,
          run_label: `Module ${module} — ${new Date().toLocaleDateString()}`,
        }),
      });
      if (beta != null) onRefresh();
      return data;
    } catch { return null; }
  }, [buildParams, element.id, onRefresh]);

  /* ── Overview Tab ── */
  const OverviewTab = () => {
    const [editing, setEditing] = useState(false);
    const [editingEnv, setEditingEnv] = useState(false);
    const [savingProps, setSavingProps] = useState(false);
    const [savingEnv, setSavingEnv] = useState(false);
    const [fetchingClimate, setFetchingClimate] = useState(false);

    const envOverrides = element.env_overrides || {};
    const merged = { ...env, ...envOverrides };

    const [props, setProps] = useState({
      exposure_class: element.exposure_class || 'XC3',
      cement_type: element.cement_type || 'CEM_I',
      wc_ratio: element.wc_ratio ?? 0.50,
      cover_mean: element.cover_mean ?? 35,
      cover_std: element.cover_std ?? 8,
      design_life: element.design_life ?? 50,
      commissioning_year: element.commissioning_year ?? '',
      racc_mean: element.racc_mean ?? '',
      racc_std: element.racc_std ?? '',
      drcm_mean: element.drcm_mean ?? '',
      drcm_std: element.drcm_std ?? '',
      orientation: element.orientation || '',
      custom_field_name: element.custom_field_name || '',
      custom_field_value: element.custom_field_value || '',
      element_type: element.element_type || '',
      notes: element.notes || '',
    });

    const [envForm, setEnvForm] = useState({
      rh_mean: merged.rh_mean ?? '', rh_std: merged.rh_std ?? '',
      rh_min: merged.rh_min ?? '', rh_max: merged.rh_max ?? '',
      rh_distribution: merged.rh_distribution || 'beta',
      temperature_mean: merged.temperature_mean ?? '', temperature_std: merged.temperature_std ?? '',
      co2_mean: merged.co2_mean != null ? String(merged.co2_mean) : '0.00082',
      co2_std: merged.co2_std != null ? String(merged.co2_std) : '0.0001',
      tow: merged.tow ?? 0, psr: merged.psr ?? 0,
    });

    const XC_DESCS = {
      XC1: 'Dry / Indoor — low RH',
      XC2: 'Wet, rarely dry — buried (piles, foundations) — RH ~100%',
      XC3: 'Moderate humidity — sheltered exterior — Open-Meteo annual RH',
      XC4: 'Cyclic wet/dry — exposed exterior — needs ToW & pSR',
    };

    const ORIENT_OPTIONS = ['', 'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const showXC4 = props.exposure_class === 'XC4';
    const xcDesc = XC_DESCS[props.exposure_class] || '';

    // When exposure class changes in edit mode, auto-adjust env defaults
    const handleExposureChange = (newXC) => {
      setProps(p => ({ ...p, exposure_class: newXC }));
      if (newXC === 'XC1') {
        setEnvForm(f => ({ ...f, rh_mean: 45, rh_std: 8, rh_min: 20, rh_max: 70, tow: 0, psr: 0 }));
      } else if (newXC === 'XC2') {
        setEnvForm(f => ({ ...f, rh_mean: 99, rh_std: 1, rh_min: 95, rh_max: 100, tow: 0, psr: 0 }));
      } else if (newXC === 'XC3') {
        // Revert to asset/Open-Meteo data
        setEnvForm(f => ({ ...f, rh_mean: env.rh_mean ?? 70, rh_std: env.rh_std ?? 12, rh_min: env.rh_min ?? 40, rh_max: env.rh_max ?? 100, tow: 0, psr: 0 }));
      } else if (newXC === 'XC4') {
        setEnvForm(f => ({ ...f, rh_mean: env.rh_mean ?? 80, rh_std: env.rh_std ?? 14, rh_min: env.rh_min ?? 40, rh_max: env.rh_max ?? 100, tow: env.tow ?? 0.3, psr: 0 }));
      }
    };

    const handleSaveProps = async () => {
      setSavingProps(true);
      try {
        const body = {};
        for (const [k, v] of Object.entries(props)) {
          if (v !== '' && v !== null && v !== undefined) body[k] = typeof v === 'string' ? v : Number(v);
        }
        await fetchSln(`/api/sln/elements/${element.id}`, { method: 'PATCH', body: JSON.stringify(body) });
        setEditing(false);
        onRefresh();
      } catch (e) { alert(e.message); }
      setSavingProps(false);
    };

    const handleSaveEnv = async () => {
      setSavingEnv(true);
      try {
        const overrides = {
          rh_mean: Number(envForm.rh_mean), rh_std: Number(envForm.rh_std),
          rh_min: Number(envForm.rh_min), rh_max: Number(envForm.rh_max),
          rh_distribution: envForm.rh_distribution,
          temperature_mean: Number(envForm.temperature_mean), temperature_std: Number(envForm.temperature_std),
          co2_mean: Number(envForm.co2_mean), co2_std: Number(envForm.co2_std),
          tow: Number(envForm.tow), psr: Number(envForm.psr),
        };
        await fetchSln(`/api/sln/elements/${element.id}`, {
          method: 'PATCH', body: JSON.stringify({ env_overrides: overrides }),
        });
        setEditingEnv(false);
        onRefresh();
      } catch (e) { alert(e.message); }
      setSavingEnv(false);
    };

    const fetchClimateForAsset = async () => {
      if (!asset?.location) return;
      setFetchingClimate(true);
      try {
        const data = await fetchSln('/api/sln/climate-lookup', {
          method: 'POST', body: JSON.stringify({ location: asset.location }),
        });
        setEnvForm(f => ({
          ...f,
          temperature_mean: data.temperature_mean, temperature_std: data.temperature_std,
          rh_mean: data.rh_mean, rh_std: data.rh_std, rh_min: data.rh_min, rh_max: data.rh_max,
          tow: data.tow ?? f.tow,
        }));
        // Also look up pSR for element orientation
        if (props.orientation && data.psr_by_direction) {
          const psr = data.psr_by_direction[props.orientation] || 0;
          setEnvForm(f => ({ ...f, psr: psr }));
        }
      } catch (e) { alert('Climate lookup failed: ' + e.message); }
      setFetchingClimate(false);
    };

    const WC_OPTIONS = [0.40, 0.45, 0.50, 0.55, 0.60];
    const XC_OPTIONS = ['XC1', 'XC2', 'XC3', 'XC4', 'XS1', 'XS2', 'XS3', 'XD1', 'XD2', 'XD3'];
    const fieldStyle = { background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text };
    const lookupRacc = getRacc(props.cement_type, props.wc_ratio);
    const raccDisplay = props.racc_mean || lookupRacc[0];
    const raccStdDisplay = props.racc_std || lookupRacc[1];

    return (
      <div className="space-y-5" data-testid="sln-el-overview">
        {/* Status bar */}
        <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: statusMeta.bg, border: `1px solid ${statusMeta.color}30` }}>
          <div className="w-4 h-4 rounded-full" style={{ background: statusMeta.color }} />
          <div>
            <p className="text-sm font-bold" style={{ color: statusMeta.color }}>{statusMeta.label}</p>
            <p className="text-[10px]" style={{ color: t.textMuted }}>
              {element.current_beta != null ? `\u03B2 = ${element.current_beta}` : 'No analysis run yet'}
              {element.last_inspection_date ? ` | Last inspected: ${element.last_inspection_date}` : ''}
            </p>
          </div>
          {element.next_inspection_due && (
            <div className="ml-auto text-right">
              <p className="text-[10px] font-semibold" style={{ color: t.textDim }}>Next inspection</p>
              <p className="text-xs font-bold" style={{ color: t.text }}>{new Date(element.next_inspection_due).toLocaleDateString()}</p>
            </div>
          )}
        </div>

        {/* UID */}
        <div className="flex items-center gap-2 text-xs p-3 rounded-lg" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
          <span style={{ color: t.textDim }}>UID:</span>
          <code className="font-mono font-semibold" style={{ color: t.accent }}>{element.uid}</code>
        </div>

        {/* Element Properties */}
        <div className="rounded-lg p-4" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: t.textDim }}>Element Properties</h3>
            {!isExpired && !editing && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} style={{ color: t.accent }} data-testid="sln-edit-props-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            )}
            {editing && (
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveProps} disabled={savingProps} style={{ background: t.accent, color: '#fff' }} data-testid="sln-save-props-btn">
                  {savingProps ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  <span className="ml-1">Save</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)} style={{ color: t.textMuted }}>Cancel</Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
            <Field label="Element Type" editing={editing} t={t}>
              {editing ? <input value={props.element_type} onChange={e => setProps(p => ({ ...p, element_type: e.target.value }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-element-type" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.element_type || '-'}</span>}
            </Field>
            <Field label="Exposure Class" editing={editing} t={t}>
              {editing ? <select value={props.exposure_class} onChange={e => handleExposureChange(e.target.value)} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-exposure">
                {XC_OPTIONS.map(x => <option key={x} value={x}>{x}</option>)}
              </select> : <span className="text-sm font-bold" style={{ color: t.text }}>{element.exposure_class}</span>}
            </Field>
            <Field label="Design Life (yr)" editing={editing} t={t}>
              {editing ? <input type="number" value={props.design_life} onChange={e => setProps(p => ({ ...p, design_life: e.target.value }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-design-life" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.design_life} yr</span>}
            </Field>
            <Field label="Commissioning Year" editing={editing} t={t}>
              {editing ? <input type="number" value={props.commissioning_year} onChange={e => setProps(p => ({ ...p, commissioning_year: e.target.value }))} placeholder="e.g. 1997" className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-comm-year" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.commissioning_year || '-'}</span>}
            </Field>
            <Field label="Cement Type" editing={editing} t={t}>
              {editing ? <select value={props.cement_type} onChange={e => setProps(p => ({ ...p, cement_type: e.target.value }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-cement">
                {Object.entries(CEMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select> : <span className="text-sm font-bold" style={{ color: t.text }}>{CEMENT_LABELS[element.cement_type] || element.cement_type}</span>}
            </Field>
            <Field label="w/c Ratio" editing={editing} t={t}>
              {editing ? <select value={props.wc_ratio} onChange={e => setProps(p => ({ ...p, wc_ratio: parseFloat(e.target.value) }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-wc">
                {WC_OPTIONS.map(w => <option key={w} value={w}>{w.toFixed(2)}</option>)}
              </select> : <span className="text-sm font-bold" style={{ color: t.text }}>{element.wc_ratio}</span>}
            </Field>
            <Field label="Cover Mean (mm)" editing={editing} t={t}>
              {editing ? <input type="number" step="0.1" value={props.cover_mean} onChange={e => setProps(p => ({ ...p, cover_mean: e.target.value }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-cover-mean" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.cover_mean} mm</span>}
            </Field>
            <Field label="Cover Std (mm)" editing={editing} t={t}>
              {editing ? <input type="number" step="0.1" value={props.cover_std} onChange={e => setProps(p => ({ ...p, cover_std: e.target.value }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-cover-std" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.cover_std} mm</span>}
            </Field>
            <Field label={<>R<sub>ACC,0</sub> Mean</>} editing={editing} t={t} hint="Carbonation resistance">
              {editing ? <input type="number" step="1" value={props.racc_mean} onChange={e => setProps(p => ({ ...p, racc_mean: e.target.value }))} placeholder={`${lookupRacc[0]} (auto)`} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-racc-mean" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{raccDisplay}</span>}
            </Field>
            <Field label={<>R<sub>ACC,0</sub> Std</>} editing={editing} t={t}>
              {editing ? <input type="number" step="1" value={props.racc_std} onChange={e => setProps(p => ({ ...p, racc_std: e.target.value }))} placeholder={`${lookupRacc[1]} (auto)`} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-racc-std" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{raccStdDisplay}</span>}
            </Field>
            <Field label={<>D<sub>RCM,0</sub> Mean</>} editing={editing} t={t} hint="Chloride migration">
              {editing ? <input type="number" step="0.1" value={props.drcm_mean} onChange={e => setProps(p => ({ ...p, drcm_mean: e.target.value }))} placeholder="e.g. 12.0" className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-drcm-mean" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.drcm_mean || '-'}</span>}
            </Field>
            <Field label={<>D<sub>RCM,0</sub> Std</>} editing={editing} t={t}>
              {editing ? <input type="number" step="0.1" value={props.drcm_std} onChange={e => setProps(p => ({ ...p, drcm_std: e.target.value }))} placeholder="e.g. 3.0" className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-drcm-std" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.drcm_std || '-'}</span>}
            </Field>
            <Field label="Orientation" editing={editing} t={t} hint="For pSR (driving rain)">
              {editing ? <select value={props.orientation} onChange={e => setProps(p => ({ ...p, orientation: e.target.value }))} className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-orientation">
                {ORIENT_OPTIONS.map(o => <option key={o} value={o}>{o || '— none —'}</option>)}
              </select> : <span className="text-sm font-bold" style={{ color: t.text }}>{element.orientation || '-'}</span>}
            </Field>
            <Field label="Custom Field Name" editing={editing} t={t}>
              {editing ? <input value={props.custom_field_name} onChange={e => setProps(p => ({ ...p, custom_field_name: e.target.value }))} placeholder="e.g. Rebar grade" className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-custom-name" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.custom_field_name || '-'}</span>}
            </Field>
            <Field label={props.custom_field_name || element.custom_field_name || 'Custom Value'} editing={editing} t={t}>
              {editing ? <input value={props.custom_field_value} onChange={e => setProps(p => ({ ...p, custom_field_value: e.target.value }))} placeholder="Value" className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-custom-value" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{element.custom_field_value || '-'}</span>}
            </Field>
            <div className="col-span-2">
              <Field label="Notes" editing={editing} t={t}>
                {editing ? <input value={props.notes} onChange={e => setProps(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes" className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-prop-notes" />
                  : <span className="text-sm" style={{ color: t.textMuted }}>{element.notes || '-'}</span>}
              </Field>
            </div>
          </div>
        </div>

        {/* Environmental Conditions (editable, exposure-class aware) */}
        <div className="rounded-lg p-4" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Thermometer className="w-3.5 h-3.5" style={{ color: t.accent }} />
              <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: t.textDim }}>Environmental Conditions</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: t.inputBg, color: t.textDim }}>
                {envOverrides.rh_mean != null ? 'Element override' : `From asset${env.data_source === 'open-meteo' ? ' (Open-Meteo)' : ''}`}
              </span>
              {!isExpired && !editingEnv && (
                <Button size="sm" variant="ghost" onClick={() => setEditingEnv(true)} style={{ color: t.accent }} data-testid="sln-edit-env-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              )}
              {editingEnv && (
                <div className="flex gap-2">
                  {asset?.location && (
                    <Button size="sm" variant="ghost" onClick={fetchClimateForAsset} disabled={fetchingClimate}
                      style={{ color: t.textMuted, fontSize: '10px' }} data-testid="sln-fetch-climate">
                      {fetchingClimate ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      <span className="ml-1">Fetch from Open-Meteo</span>
                    </Button>
                  )}
                  <Button size="sm" onClick={handleSaveEnv} disabled={savingEnv} style={{ background: t.accent, color: '#fff' }} data-testid="sln-save-env-btn">
                    {savingEnv ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    <span className="ml-1">Save</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingEnv(false)} style={{ color: t.textMuted }}>Cancel</Button>
                </div>
              )}
            </div>
          </div>
          {xcDesc && (
            <p className="text-[10px] mb-3 px-2 py-1.5 rounded" style={{ background: t.inputBg, color: t.textMuted }}>
              {props.exposure_class}: {xcDesc}
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
            <Field label="Temperature Mean" editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.1" value={envForm.temperature_mean} onChange={e => setEnvForm(f => ({ ...f, temperature_mean: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-temp-mean" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.temperature_mean ?? '-'} {"\u00B0C"}</span>}
            </Field>
            <Field label="Temperature Std" editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.1" value={envForm.temperature_std} onChange={e => setEnvForm(f => ({ ...f, temperature_std: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-temp-std" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.temperature_std ?? '-'} {"\u00B0C"}</span>}
            </Field>
            <Field label="RH Mean (%)" editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.1" value={envForm.rh_mean} onChange={e => setEnvForm(f => ({ ...f, rh_mean: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-rh-mean" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.rh_mean ?? '-'}%</span>}
            </Field>
            <Field label="RH Std (%)" editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.1" value={envForm.rh_std} onChange={e => setEnvForm(f => ({ ...f, rh_std: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-rh-std" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.rh_std ?? '-'}%</span>}
            </Field>
            <Field label="RH Min (%)" editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.1" value={envForm.rh_min} onChange={e => setEnvForm(f => ({ ...f, rh_min: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.rh_min ?? '-'}%</span>}
            </Field>
            <Field label="RH Max (%)" editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.1" value={envForm.rh_max} onChange={e => setEnvForm(f => ({ ...f, rh_max: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.rh_max ?? '-'}%</span>}
            </Field>
            <Field label={<>CO<sub>2</sub> (kg/m<sup>3</sup>)</>} editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.00001" value={envForm.co2_mean} onChange={e => setEnvForm(f => ({ ...f, co2_mean: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-co2" />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.co2_mean ?? '-'}</span>}
            </Field>
            <Field label={<>CO<sub>2</sub> Std (kg/m<sup>3</sup>)</>} editing={editingEnv} t={t}>
              {editingEnv ? <input type="number" step="0.00001" value={envForm.co2_std} onChange={e => setEnvForm(f => ({ ...f, co2_std: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} />
                : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.co2_std ?? '-'}</span>}
            </Field>
            {showXC4 && (
              <>
                <Field label="ToW (Time of Wetness)" editing={editingEnv} t={t} hint="(wet days > 2.5mm) / 365">
                  {editingEnv ? <input type="number" step="0.001" value={envForm.tow} onChange={e => setEnvForm(f => ({ ...f, tow: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-tow" />
                    : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.tow ?? '-'}</span>}
                </Field>
                <Field label="pSR (Driving Rain Prob.)" editing={editingEnv} t={t} hint={props.orientation ? `Dir: ${props.orientation}` : 'Set orientation first'}>
                  {editingEnv ? <input type="number" step="0.001" value={envForm.psr} onChange={e => setEnvForm(f => ({ ...f, psr: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm rounded" style={fieldStyle} data-testid="sln-env-psr" />
                    : <span className="text-sm font-bold" style={{ color: t.text }}>{merged.psr ?? '-'}</span>}
                </Field>
              </>
            )}
            {env.latitude && (
              <Field label="Coordinates" t={t}>
                <span className="text-sm font-bold" style={{ color: t.text }}>{env.latitude}, {env.longitude}</span>
              </Field>
            )}
          </div>
          {env.psr_by_direction && showXC4 && !editingEnv && (
            <div className="mt-3 p-2 rounded" style={{ background: t.inputBg }}>
              <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: t.textDim }}>pSR by Wind Direction (Open-Meteo)</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(env.psr_by_direction).map(([dir, val]) => (
                  <span key={dir} className="text-[10px] px-1.5 py-0.5 rounded" style={{
                    background: props.orientation === dir ? t.accent + '30' : 'transparent',
                    color: props.orientation === dir ? t.accent : t.textDim,
                    border: `1px solid ${props.orientation === dir ? t.accent : t.cardBorder}`,
                    fontWeight: props.orientation === dir ? 700 : 400,
                  }}>{dir}: {(val * 100).toFixed(1)}%</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Counts */}
        <div className="flex items-center gap-4 text-[10px]" style={{ color: t.textDim }}>
          <span>{inspections.length} inspection{inspections.length !== 1 ? 's' : ''}</span>
          <span>{runs.length} engine run{runs.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    );
  };

  /* ── Baseline Forecast Tab (auto-compute) ── */
  const PrioriTab = () => {
    const cached = element.cached_baseline;
    const [computing, setComputing] = useState(false);
    const [result, setResult] = useState(cached || null);
    const [error, setError] = useState(null);
    const hasComputed = useRef(false);

    useEffect(() => {
      if (!result && !hasComputed.current && !computing) {
        hasComputed.current = true;
        setComputing(true);
        autoCompute('A').then(data => {
          if (data) setResult(data);
          else setError('Engine computation failed. Check element properties.');
        }).catch(() => setError('Engine error')).finally(() => setComputing(false));
      }
    }, [result, computing]);

    const recompute = async () => {
      setComputing(true); setError(null); setResult(null);
      try {
        const data = await autoCompute('A');
        if (data) setResult(data); else setError('Computation failed');
      } catch { setError('Engine error'); }
      setComputing(false);
    };

    return (
      <div className="space-y-4" data-testid="sln-el-priori">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold" style={{ color: t.text }}>Baseline Forecast (Module A)</h3>
            <p className="text-xs" style={{ color: t.textDim }}>
              A priori service life prediction using element properties and environmental conditions.
            </p>
          </div>
          {result && !computing && (
            <Button size="sm" variant="ghost" onClick={recompute} style={{ color: t.textMuted }} data-testid="sln-recompute-priori">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recompute
            </Button>
          )}
        </div>
        {computing && (
          <div className="flex items-center gap-3 py-10 justify-center rounded-lg" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: t.accent }} />
            <span className="text-sm" style={{ color: t.textMuted }}>Computing baseline forecast...</span>
          </div>
        )}
        {error && !computing && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <AlertTriangle className="w-4 h-4" style={{ color: '#ef4444' }} />
            <p className="text-xs" style={{ color: '#ef4444' }}>{error}</p>
            <Button size="sm" variant="ghost" onClick={recompute} className="ml-auto" style={{ color: t.accent }}>Retry</Button>
          </div>
        )}
        {result && !computing && <EngineResults result={result} module="A" t={t} />}
      </div>
    );
  };

  /* ── Inspection Tab with CRUD ── */
  const InspectionTab = () => {
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ inspection_date: new Date().toISOString().split('T')[0], inspector: '', method: 'phenolphthalein', readings_str: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);

    const openEditForm = (insp) => {
      setEditingId(insp.id);
      setForm({
        inspection_date: insp.inspection_date,
        inspector: insp.inspector || '',
        method: insp.method || 'phenolphthalein',
        readings_str: (insp.readings || []).join(', '),
        notes: insp.notes || '',
      });
      setFormOpen(true);
    };

    const resetForm = () => {
      setEditingId(null);
      setForm({ inspection_date: new Date().toISOString().split('T')[0], inspector: '', method: 'phenolphthalein', readings_str: '', notes: '' });
      setFormOpen(false);
    };

    const submitInspection = async () => {
      const readings = form.readings_str.split(/[,;\s]+/).map(Number).filter(n => !isNaN(n) && n > 0);
      if (readings.length === 0) return;
      setSubmitting(true);
      try {
        if (editingId) {
          await fetchSln(`/api/sln/inspections/${editingId}`, {
            method: 'PATCH', body: JSON.stringify({ ...form, readings }),
          });
        } else {
          await fetchSln(`/api/sln/elements/${element.id}/inspections`, {
            method: 'POST', body: JSON.stringify({ ...form, readings }),
          });
        }
        await loadInspections();
        resetForm();
      } catch (e) { alert(e.message); }
      setSubmitting(false);
    };

    const deleteInspection = async (id) => {
      if (!window.confirm('Delete this inspection record?')) return;
      try {
        await fetchSln(`/api/sln/inspections/${id}`, { method: 'DELETE' });
        await loadInspections();
      } catch (e) { alert(e.message); }
    };

    return (
      <div className="space-y-5" data-testid="sln-el-inspection">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold" style={{ color: t.text }}>Inspection Data (Module B)</h3>
            <p className="text-xs" style={{ color: t.textDim }}>Measured carbonation depth readings for this element</p>
          </div>
          {!isExpired && !formOpen && (
            <Button size="sm" onClick={() => { resetForm(); setFormOpen(true); }} style={{ background: t.accent, color: '#fff' }} data-testid="sln-add-inspection">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Reading
            </Button>
          )}
        </div>

        {formOpen && (
          <div className="rounded-lg p-4 space-y-3" style={{ background: t.card, border: `1px solid ${t.accent}` }}>
            <p className="text-xs font-bold" style={{ color: t.accent }}>{editingId ? 'Edit Inspection' : 'New Inspection'}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] block mb-0.5" style={{ color: t.textDim }}>Date</label>
                <input type="date" value={form.inspection_date} onChange={e => setForm(f => ({ ...f, inspection_date: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm rounded" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} data-testid="sln-insp-date" />
              </div>
              <div>
                <label className="text-[10px] block mb-0.5" style={{ color: t.textDim }}>Inspector</label>
                <input type="text" value={form.inspector} onChange={e => setForm(f => ({ ...f, inspector: e.target.value }))} placeholder="Name"
                  className="w-full px-2 py-1.5 text-sm rounded" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
              </div>
              <div>
                <label className="text-[10px] block mb-0.5" style={{ color: t.textDim }}>Method</label>
                <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm rounded" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}>
                  <option value="phenolphthalein">Phenolphthalein</option>
                  <option value="profile_grinding">Profile grinding</option>
                  <option value="covermeter">Covermeter</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] block mb-0.5" style={{ color: t.textDim }}>Notes</label>
                <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional"
                  className="w-full px-2 py-1.5 text-sm rounded" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
              </div>
            </div>
            <div>
              <label className="text-[10px] block mb-0.5" style={{ color: t.textDim }}>Readings (mm, comma separated)</label>
              <input type="text" value={form.readings_str} onChange={e => setForm(f => ({ ...f, readings_str: e.target.value }))}
                placeholder="e.g. 6.2, 7.1, 5.8, 6.9, 7.3"
                className="w-full px-2 py-1.5 text-sm rounded" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} data-testid="sln-insp-readings" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={submitInspection} disabled={submitting} style={{ background: t.accent, color: '#fff' }} data-testid="sln-insp-submit">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                <span className="ml-1.5">{editingId ? 'Update' : 'Save Reading'}</span>
              </Button>
              <Button size="sm" variant="ghost" onClick={resetForm} style={{ color: t.textMuted }}>Cancel</Button>
            </div>
          </div>
        )}

        {inspections.length > 0 ? (
          <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${t.cardBorder}` }}>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: t.inputBg }}>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: t.textDim }}>Date</th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: t.textDim }}>Method</th>
                  <th className="text-right px-3 py-2 font-semibold" style={{ color: t.textDim }}>n</th>
                  <th className="text-right px-3 py-2 font-semibold" style={{ color: t.textDim }}>Mean (mm)</th>
                  <th className="text-right px-3 py-2 font-semibold" style={{ color: t.textDim }}>Std (mm)</th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: t.textDim }}>Inspector</th>
                  {!isExpired && <th className="text-right px-3 py-2 font-semibold" style={{ color: t.textDim }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {inspections.map(insp => (
                  <tr key={insp.id} style={{ borderTop: `1px solid ${t.cardBorder}` }}>
                    <td className="px-3 py-2" style={{ color: t.text }}>{insp.inspection_date}</td>
                    <td className="px-3 py-2" style={{ color: t.textMuted }}>{insp.method}</td>
                    <td className="px-3 py-2 text-right" style={{ color: t.textMuted }}>{insp.n_readings}</td>
                    <td className="px-3 py-2 text-right font-semibold" style={{ color: t.text }}>{insp.mean}</td>
                    <td className="px-3 py-2 text-right" style={{ color: t.textMuted }}>{insp.std_dev}</td>
                    <td className="px-3 py-2" style={{ color: t.textDim }}>{insp.inspector || '-'}</td>
                    {!isExpired && (
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => openEditForm(insp)} className="p-1 mr-1 rounded hover:opacity-70" style={{ color: t.accent }} data-testid={`sln-edit-insp-${insp.id}`}>
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button onClick={() => deleteInspection(insp.id)} className="p-1 rounded hover:opacity-70" style={{ color: '#ef4444' }} data-testid={`sln-del-insp-${insp.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 rounded-lg border-2 border-dashed" style={{ borderColor: t.cardBorder }}>
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: t.textDim }} />
            <p className="text-sm" style={{ color: t.textDim }}>No inspection data recorded yet</p>
          </div>
        )}
      </div>
    );
  };

  /* ── Updated Forecast Tab (auto-compute) ── */
  const PosterioriTab = () => {
    const hasInsp = inspections.length > 0;
    const cached = element.cached_updated;
    const [computing, setComputing] = useState(false);
    const [result, setResult] = useState(cached || null);
    const [error, setError] = useState(null);
    const hasComputed = useRef(false);

    useEffect(() => {
      if (hasInsp && !result && !hasComputed.current && !computing) {
        hasComputed.current = true;
        setComputing(true);
        autoCompute('C').then(data => {
          if (data) setResult(data);
          else setError('Computation failed. Check inspection data.');
        }).catch(() => setError('Engine error')).finally(() => setComputing(false));
      }
    }, [hasInsp, result, computing]);

    const recompute = async () => {
      setComputing(true); setError(null); setResult(null);
      try {
        const data = await autoCompute('C');
        if (data) setResult(data); else setError('Computation failed');
      } catch { setError('Engine error'); }
      setComputing(false);
    };

    return (
      <div className="space-y-4" data-testid="sln-el-posteriori">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold" style={{ color: t.text }}>Inspection-Updated Forecast (Module C)</h3>
            <p className="text-xs" style={{ color: t.textDim }}>
              Combines baseline with field data for a data-enhanced prediction.
            </p>
          </div>
          {result && !computing && hasInsp && (
            <Button size="sm" variant="ghost" onClick={recompute} style={{ color: t.textMuted }} data-testid="sln-recompute-posteriori">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recompute
            </Button>
          )}
        </div>
        {!hasInsp && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <p className="text-xs" style={{ color: '#f59e0b' }}>Add inspection readings in the Inspection Data tab before viewing an updated forecast.</p>
          </div>
        )}
        {hasInsp && !computing && !result && !error && (
          <div className="rounded-lg p-3" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: t.accent }}>Using Latest Inspection</p>
            <p className="text-xs" style={{ color: t.text }}>
              Date: {inspections[0].inspection_date} | Mean: {inspections[0].mean} mm | Std: {inspections[0].std_dev} mm | n={inspections[0].n_readings}
            </p>
          </div>
        )}
        {computing && (
          <div className="flex items-center gap-3 py-10 justify-center rounded-lg" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#6366f1' }} />
            <span className="text-sm" style={{ color: t.textMuted }}>Computing updated forecast...</span>
          </div>
        )}
        {error && !computing && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <AlertTriangle className="w-4 h-4" style={{ color: '#ef4444' }} />
            <p className="text-xs" style={{ color: '#ef4444' }}>{error}</p>
            {hasInsp && <Button size="sm" variant="ghost" onClick={recompute} className="ml-auto" style={{ color: '#6366f1' }}>Retry</Button>}
          </div>
        )}
        {result && !computing && <EngineResults result={result} module="C" t={t} />}
      </div>
    );
  };

  const PlaceholderTab = ({ title, description, icon: Icon }) => (
    <div className="flex flex-col items-center justify-center py-16 rounded-xl border-2 border-dashed" style={{ borderColor: t.cardBorder }}>
      <Icon className="w-10 h-10 mb-3 opacity-20" style={{ color: t.textDim }} />
      <p className="text-sm font-semibold" style={{ color: t.textMuted }}>{title}</p>
      <p className="text-xs mt-1" style={{ color: t.textDim }}>{description}</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="sln-element-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded-md" style={{ background: t.inputBg }} data-testid="sln-el-back">
            <ChevronLeft className="w-4 h-4" style={{ color: t.textMuted }} />
          </button>
          <div>
            <h2 className="text-base font-bold" style={{ color: t.text }}>{element.element_name}</h2>
            <p className="text-[10px] font-mono" style={{ color: t.textDim }}>{element.uid}</p>
          </div>
        </div>
        <StatusBadgeInline status={element.status} beta={element.current_beta} t={t} />
      </div>
      <div className="flex gap-0.5 overflow-x-auto pb-0.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => !tb.disabled && setTab(tb.id)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap"
            style={{
              color: tab === tb.id ? t.accent : (tb.disabled ? t.textDim : t.textMuted),
              borderBottom: tab === tb.id ? `2px solid ${t.accent}` : '2px solid transparent',
              opacity: tb.disabled ? 0.4 : 1, cursor: tb.disabled ? 'not-allowed' : 'pointer',
            }}
            data-testid={`sln-el-tab-${tb.id}`}>
            <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            {tb.disabled && <span className="text-[8px] ml-0.5 px-1 py-0.5 rounded" style={{ background: t.inputBg }}>soon</span>}
          </button>
        ))}
      </div>
      {tab === 'overview' && <OverviewTab />}
      {tab === 'priori' && <PrioriTab />}
      {tab === 'inspection' && <InspectionTab />}
      {tab === 'posteriori' && <PosterioriTab />}
      {tab === 'maintenance' && <PlaceholderTab title="Maintenance Planning" description="Intervention scheduling and cost estimation — coming soon" icon={Wrench} />}
      {tab === 'financial' && <PlaceholderTab title="Financial Analysis" description="NPV-based optimal intervention window — coming soon" icon={DollarSign} />}
    </div>
  );
};

/* ── Sub-components ── */

const Field = ({ label, children, editing, t, hint }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: t.textDim }}>
      {label}
      {hint && editing && <span className="normal-case tracking-normal font-normal ml-1" style={{ color: t.textDim }}>({hint})</span>}
    </p>
    {children}
  </div>
);

const StatusBadgeInline = ({ status, beta, t }) => {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <div className="flex items-center gap-2">
      {beta != null && <span className="text-sm font-bold" style={{ color: meta.color }}>{"\u03B2"} {beta}</span>}
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
    </div>
  );
};

/* ── Theme-aware SVG chart ── */
const NexusChart = ({ data, yKey, label, color = '#10b981', secondData, secondColor = '#6366f1', height = 220, showThreshold, thresholdVal, verticalLineX, yUnit, t }) => {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  if (!data || data.length === 0) return null;

  const isDark = t.bg === '#080a0f' || t.bg.startsWith('#0');
  const gridMajor = isDark ? 'rgba(255,255,255,0.08)' : '#ddd';
  const gridMinor = isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0';
  const labelColor = isDark ? 'rgba(255,255,255,0.4)' : '#999';
  const titleColor = isDark ? 'rgba(255,255,255,0.7)' : '#333';
  const tooltipBg = isDark ? 'rgba(0,0,0,0.85)' : '#1f2937';
  const tooltipText = '#d1d5db';

  const pad = { t: 28, r: 28, b: 38, l: 56 };
  const w = 520, h = height;
  const iw = w - pad.l - pad.r, ih = h - pad.t - pad.b;

  const allVals = [...data.map(d => d[yKey]), ...(secondData || []).map(d => d[yKey])];
  const xMin = data[0].year, xMax = data[data.length - 1].year;

  const isPf = yKey === 'pf';
  const isMm = yUnit === 'mm';
  const rawMax = Math.max(...allVals);
  const yMin = 0;
  let majorStep;
  if (isPf) majorStep = 0.10;
  else if (isMm) majorStep = 5;
  else majorStep = rawMax > 3 ? 1.0 : 0.5;
  const minorStep = majorStep / 2;
  let yMax = Math.ceil(rawMax / majorStep) * majorStep || majorStep;
  if (yMax - rawMax < majorStep * 0.15) yMax += majorStep;

  const sx = x => pad.l + ((x - xMin) / (xMax - xMin || 1)) * iw;
  const sy = y => pad.t + ih - ((y - yMin) / (yMax - yMin || 1)) * ih;
  const pts = d => d.map(p => `${sx(p.year)},${sy(p[yKey])}`).join(' ');

  const majorYLines = [], minorYLines = [];
  for (let v = yMin; v <= yMax + minorStep * 0.01; v += minorStep) {
    const rounded = Math.round(v / minorStep) * minorStep;
    const isMajor = Math.abs(rounded / majorStep - Math.round(rounded / majorStep)) < 0.001;
    if (isMajor) majorYLines.push(rounded); else minorYLines.push(rounded);
  }
  const majorXStep = 10, minorXStep = 5;
  const majorXLines = [], minorXLines = [];
  for (let yr = Math.ceil(xMin / minorXStep) * minorXStep; yr <= xMax; yr += minorXStep) {
    if (yr % majorXStep === 0) majorXLines.push(yr); else minorXLines.push(yr);
  }

  const handleMouse = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width * w;
    const hovYear = xMin + ((mx - pad.l) / iw) * (xMax - xMin);
    let best = null, bestDist = Infinity;
    for (const d of data) { const dist = Math.abs(d.year - hovYear); if (dist < bestDist) { bestDist = dist; best = d; } }
    let bestSecond = null;
    if (secondData) { for (const d of secondData) { if (d.year === best?.year) { bestSecond = d; break; } } }
    if (best && Math.abs(sx(best.year) - mx) < iw / (data.length * 0.6)) {
      setHover({ point: best, second: bestSecond, px: sx(best.year), py: sy(best[yKey]) });
    } else { setHover(null); }
  };

  const fmtVal = v => isPf ? (v * 100).toFixed(1) + '%' : isMm ? v.toFixed(1) + ' mm' : v.toFixed(2);

  return (
    <div className="rounded-lg p-3" style={{ background: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa', border: `1px solid ${t.cardBorder}` }} data-testid={`sln-chart-${yKey}`}>
      <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: height }}
        onMouseMove={handleMouse} onMouseLeave={() => setHover(null)}>
        {minorYLines.map(v => <line key={`my${v}`} x1={pad.l} x2={w - pad.r} y1={sy(v)} y2={sy(v)} stroke={gridMinor} strokeWidth="0.5" />)}
        {majorYLines.map(v => (
          <g key={`Yg${v}`}>
            <line x1={pad.l} x2={w - pad.r} y1={sy(v)} y2={sy(v)} stroke={gridMajor} strokeWidth="0.7" />
            <text x={pad.l - 6} y={sy(v) + 3} textAnchor="end" fontSize="9" fill={labelColor}>
              {isPf ? (v * 100).toFixed(0) + '%' : isMm ? v.toFixed(0) : v.toFixed(1)}
            </text>
          </g>
        ))}
        {minorXLines.map(yr => <line key={`mx${yr}`} x1={sx(yr)} x2={sx(yr)} y1={pad.t} y2={h - pad.b} stroke={gridMinor} strokeWidth="0.5" />)}
        {majorXLines.map(yr => (
          <g key={`Xg${yr}`}>
            <line x1={sx(yr)} x2={sx(yr)} y1={pad.t} y2={h - pad.b} stroke={gridMajor} strokeWidth="0.5" />
            <text x={sx(yr)} y={h - 12} textAnchor="middle" fontSize="9" fill={labelColor}>{yr}</text>
          </g>
        ))}
        {showThreshold && thresholdVal != null && (
          <>
            <line x1={pad.l} x2={w - pad.r} y1={sy(thresholdVal)} y2={sy(thresholdVal)} stroke="#ef4444" strokeWidth="1" strokeDasharray="4 2" />
            <text x={w - pad.r + 2} y={sy(thresholdVal) + 3} fontSize="8" fill="#ef4444">target</text>
          </>
        )}
        {verticalLineX != null && verticalLineX >= xMin && verticalLineX <= xMax && (
          <>
            <line x1={sx(verticalLineX)} x2={sx(verticalLineX)} y1={pad.t} y2={h - pad.b} stroke="#6366f1" strokeWidth="1.5" strokeDasharray="6 3" />
            <text x={sx(verticalLineX) + 4} y={pad.t + 10} fontSize="8" fill="#6366f1" fontWeight="600">t_insp</text>
          </>
        )}
        <polyline points={pts(data)} fill="none" stroke={color} strokeWidth="2" />
        {secondData && secondData.length > 0 && (
          <polyline points={pts(secondData)} fill="none" stroke={secondColor} strokeWidth="2" strokeDasharray="6 3" />
        )}
        {hover && (
          <>
            <line x1={hover.px} x2={hover.px} y1={pad.t} y2={h - pad.b} stroke={labelColor} strokeWidth="0.7" strokeDasharray="3 2" />
            <circle cx={hover.px} cy={hover.py} r="3.5" fill={color} stroke={isDark ? '#1a1a2e' : '#fff'} strokeWidth="1.5" />
            {hover.second && <circle cx={hover.px} cy={sy(hover.second[yKey])} r="3.5" fill={secondColor} stroke={isDark ? '#1a1a2e' : '#fff'} strokeWidth="1.5" />}
            <g transform={`translate(${hover.px + (hover.px > w * 0.7 ? -90 : 8)}, ${Math.max(pad.t + 4, hover.py - 28)})`}>
              <rect x="0" y="0" width="82" height={hover.second ? 42 : 28} rx="4" fill={tooltipBg} fillOpacity="0.95" />
              <text x="6" y="13" fontSize="9" fill={tooltipText} fontWeight="600">Year {hover.point.year}</text>
              <text x="6" y="24" fontSize="9" fill={color}>{fmtVal(hover.point[yKey])}</text>
              {hover.second && <text x="6" y="36" fontSize="9" fill={secondColor}>{fmtVal(hover.second[yKey])}</text>}
            </g>
          </>
        )}
        <text x={pad.l} y={16} fontSize="10" fontWeight="600" fill={titleColor}>{label}</text>
      </svg>
      <div className="flex gap-4 mt-1 px-2">
        <span className="text-[10px] flex items-center gap-1.5" style={{ color: t.textDim }}>
          <span className="w-4 h-0.5 inline-block" style={{ background: color }} /> {secondData ? 'A priori' : 'Baseline'}
        </span>
        {secondData && secondData.length > 0 && (
          <span className="text-[10px] flex items-center gap-1.5" style={{ color: t.textDim }}>
            <span className="w-4 h-0.5 inline-block" style={{ borderTop: `2px dashed ${secondColor}` }} /> A posteriori
          </span>
        )}
      </div>
    </div>
  );
};

const EngineResults = ({ result, module, t }) => {
  const summary = result.summary || {};
  const baseline = result.baseline || [];
  const updated = result.updated || [];
  const hasBayes = updated.length > 0;
  const inspAge = summary.inspection_age;
  const pass = summary.status === 'PASS';
  const baseFiltered = baseline.filter(d => d.year > 0);

  return (
    <div className="space-y-3" data-testid="sln-engine-results">
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: pass ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${pass ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
        <div className="w-3 h-3 rounded-full" style={{ background: pass ? '#10b981' : '#ef4444' }} />
        <span className="text-sm font-bold" style={{ color: pass ? '#10b981' : '#ef4444' }}>{pass ? 'ADEQUATE' : 'INADEQUATE'}</span>
        <span className="text-xs ml-2" style={{ color: '#6b7280' }}>
          {"\u03B2"} = {summary.updated_beta || summary.baseline_beta} (target {"\u2265"} {summary.target_beta})
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Baseline pf', value: summary.baseline_pf != null ? `${(summary.baseline_pf * 100).toFixed(1)}%` : '-', sub: `\u03B2=${summary.baseline_beta || '-'}` },
          summary.updated_pf != null && { label: 'Updated pf', value: `${(summary.updated_pf * 100).toFixed(1)}%`, sub: `\u03B2=${summary.updated_beta}` },
          { label: 'Horizon', value: `${summary.horizon || '-'}`, sub: 'years' },
          { label: 'Method', value: summary.prior_method || '-', sub: module === 'C' ? (summary.update_method || '-') : 'a priori' },
        ].filter(Boolean).map((m, i) => (
          <div key={i} className="rounded-lg px-3 py-2" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
            <p className="text-[10px] uppercase font-semibold" style={{ color: t.textDim }}>{m.label}</p>
            <p className="text-base font-bold mt-0.5" style={{ color: t.text }}>{m.value}</p>
            <p className="text-[10px]" style={{ color: t.textDim }}>{m.sub}</p>
          </div>
        ))}
      </div>
      {baseFiltered.length > 0 && (
        <div className="space-y-3">
          <NexusChart data={baseFiltered} yKey="pf" label="Probability of Corrosion Initiation (pf)"
            color="#10b981" secondData={hasBayes ? updated : null} secondColor="#6366f1"
            verticalLineX={hasBayes ? inspAge : null} t={t} />
          <NexusChart data={baseFiltered} yKey="beta" label={`Reliability Index (\u03B2)`}
            color="#10b981" secondData={hasBayes ? updated : null} secondColor="#6366f1"
            showThreshold thresholdVal={1.5} verticalLineX={hasBayes ? inspAge : null} t={t} />
          <NexusChart data={baseline} yKey="xc_mean" label="Mean Carbonation Depth (mm)"
            color="#10b981" secondData={hasBayes ? updated : null} secondColor="#6366f1"
            yUnit="mm" t={t} />
        </div>
      )}
    </div>
  );
};

export default NexusElement;
